<template>
  <div class="settings">
    <h1 class="text-2xl my-8 font-semibold text-vp-base-900 py-6">
      {{ $gettext('My Account') }}
    </h1>
    <Form
      v-if="userFound"
      class="vp-login-form"
      :form-id="formId"
      :initial-values="user"
      @submit="handleSubmit"
    >
      <VPInput
        input-id="name"
        :label="$gettext('Name')"
      />
      <VPInput
        input-id="email"
        :label="$gettext('Email')"
      />
      <FormActionButtons
        :show-save="isFormDirty()"
        :show-cancel="isFormDirty()"
        class="form-buttons"
        @cancel="onCancel"
        @save="handleSubmit"
      />
    </Form>
  </div>
</template>

<script setup lang='ts'>
import VPInput from '@/components/form/VPInput.vue';
import { useUsersStore } from '@/stores/user';
import type { User } from '@/types/User';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { Form, useFormsStoreComposable } from 'vuniform';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';
import FormActionButtons from '@/components/form/FormActionButtons.vue';
import { LoadingState } from '@/util/LoadingState';

const userStore = useUsersStore();
const route = useRoute();
const toast = useToast();
const { $gettext } = useGettext();
const { formId, formGetValues, formsStore, isFormDirty } = useFormsStoreComposable('user-edit');

const userId = parseInt(route.params.userId as string, 10);
userStore.fetchUser(userId);

const user = computed(() => userStore.user);
const userFound = computed(() => userStore.userLoading === LoadingState.DONE);

const handleSubmit = async () => {
  const { name, email } = formGetValues<User>();
  const { user, errors } = await userStore.saveUser({
    id: userId,
    name: name,
    email: email,
  });

  if (errors) {
    formsStore.SET_ERRORS({ requesterId: formId, errors });
    return;
  }

  formsStore.SET_INITIAL_VALUES({ formId: formId, values: {
    name: user.name,
    email: user.email,
  } });
  formsStore.CLEAR_FORM_DIRTY(formId);
  toast.success($gettext('Account information updated'));
};

const onCancel = async () => {
  formsStore.RESET_FIELD_VALUES(formId);
};

</script>
